import React from 'react';
import { Alert, Button } from 'antd';

import Guidance from '../common/Guidance';
import {
	ACCESS_GROUP_ONLY_HINT,
	TOO_LONG_CODE,
	TOO_LONG_TITLE,
	accessPickScope,
	confirmationSentence,
	elementPickOptions,
	elementRefusalTitle,
	groupReadsSentence,
	isElementConfirmation,
	isPickableElementRefusal,
	isUnpickableElementRefusal,
	pickNeedsAll,
	refusalSentence,
} from '../../utils/elementRefusal';

export const QUERY_REFUSAL_GUIDE = 'finding-and-lists/build-a-filter';
export const QUERY_REFUSAL_REMOVE_LABEL = 'Remove filter';
/** What an element pick says it does. */
export const ELEMENT_PICK_WHY = 'Pick the one you mean, or any of these cards. The filter then names the element itself, and keeps naming it if it is renamed.';
/** What an unknown element's notice adds to the server's sentence, where the filter stays to be edited. */
export const UNKNOWN_ELEMENT_EDIT = 'Edit the filter to name an element this collection has.';
/** What a too-long notice offers when a pick made it too long: forget the picks, and be asked again. */
export const PICK_AGAIN_LABEL = 'Pick again';

/** A candidate tag's path in words: "Health › Digestive › Stomach pain". */
export const candidatePathLabel = candidate => (
	Array.isArray(candidate?.path) && candidate.path.length
		? candidate.path.join(' › ')
		: String(candidate?.path || candidate?.tagId || '')
);

/** The refusals this answers: a tag several tags share, and an element name the server could not settle. */
export const answersRefusal = refusal => (
	(refusal?.code === 'ambiguous_tag' && Array.isArray(refusal.candidates) && refusal.candidates.length > 0)
	|| isPickableElementRefusal(refusal)
	|| isUnpickableElementRefusal(refusal)
	|| refusal?.code === 'unknown_element'
	|| refusal?.code === TOO_LONG_CODE
);

/**
 * The answer wherever the server refused a filter for a name the person can
 * settle by picking — the list (under the toolbar) and every window that
 * saves a filter (`inline`, beside its own error line):
 *
 * - **a tag name several tags share** (400 `ambiguous_tag`, deterministic
 *   filters ruling E2): the tags it could be, as buttons; a pick names THAT
 *   tag by id (the caller rewrites the text — `replaceTagName`);
 * - **an element name several elements answer to** (400
 *   `ambiguous_element`, element ids step 3, KPR-238 P4): each candidate by
 *   its label and card ("Status on Task"; "Caliber on Suppressor (global)"
 *   where two listed cards share a name — the server's `qualifier`,
 *   KPR-275), and "Any of these cards". A pick
 *   hands `onPick` one element id, or every id (the element group); the
 *   caller sends the request again with it as `elementChoices`
 *   (utils/elementRefusal.js) and the server writes the ids;
 * - **an element name that names nothing** (400 `unknown_element`): the
 *   server's sentence, which names the card where the filter is pinned to
 *   one. Nothing to pick — the filter stays to be edited;
 * - **a filter too long once picked** (400 `query_too_long`: "any of these
 *   cards" repeats its condition per card, and a filter holds 50): the
 *   server's sentence, and **Pick again** (`onRepick`) where picks made it.
 *
 * In text that decides who sees what, where an item with no value meets the
 * refused condition, only "Any of these cards" is offered — the server says
 * so (`requiresAll`, P3's security review I2), and `access` (the text) reads
 * it the server's way too. There, one element held under several names (a
 * renamed copy on a card beneath) is a confirmation: "This rule will read
 * Status on Bug and its copy State_copy on Copychild." and **Confirm**.
 *
 * The filter stays on screen instead of being dropped, and Remove filter
 * (`onRemove`, the list) is the way out. Nothing is shown for any other
 * refusal (`refusal` is the normalized one: `elementRefusalOf` for an
 * element, `{ code, value, candidates }` for a tag).
 */
export default function QueryRefusalPick({
	refusal = null,
	onPick,
	onRemove,
	className = undefined,
	// In a window that saves: a plain warning beside the form, with no guide
	// to open (the window is the place to fix it).
	inline = false,
	disabled = false,
	// Text that decides who sees what (access levels, links, public links,
	// dynamic groups): `access` is the text the refusal answers. Where a
	// missing value matches at the refused place, only "Any of these cards"
	// is offered (accessPickScope); a card the text pins exempts nothing.
	access = null,
	// A too-long text after a pick: forget the picks, and be asked again.
	onRepick = null,
}) {
	if (!answersRefusal(refusal)) return null;
	const element = refusal.code !== 'ambiguous_tag';
	const scope = typeof access === 'string' && refusal.code === 'ambiguous_element' ? accessPickScope(access, refusal) : null;
	const options = refusal.code === 'ambiguous_tag'
		? refusal.candidates.map(candidate => ({ key: candidate.tagId, value: candidate.tagId, label: candidatePathLabel(candidate) }))
		: elementPickOptions(refusal, scope);
	const picks = options.length ? (
		<span className="query-refusal__picks" role="group" aria-label={isElementConfirmation(refusal) ? 'Confirm the element it reads' : (element ? 'Pick the element it means' : 'Pick the tag it means')}>
			{options.map(option => (
				<Button key={option.key} disabled={disabled} onClick={() => onPick(option.value)}>
					{option.label}
				</Button>
			))}
		</span>
	) : null;
	const title = refusal.code === 'ambiguous_tag'
		? `“${refusal.value}” could be more than one tag`
		: elementRefusalTitle(refusal);
	let why;
	if (refusal.code === 'ambiguous_tag') why = ['Pick the one you mean. The filter then names that tag itself, and keeps naming it if it is renamed.', picks];
	// Nobody here may answer it (a candidate on a card the person cannot
	// open): the server's words, and no pick at all — the text stays.
	else if (isUnpickableElementRefusal(refusal)) why = [refusalSentence(refusal)];
	else if (isElementConfirmation(refusal)) why = [confirmationSentence(refusal), picks];
	else if (refusal.code === 'ambiguous_element') {
		why = pickNeedsAll(refusal, scope)
			? [ACCESS_GROUP_ONLY_HINT, groupReadsSentence(refusal), picks]
			: [ELEMENT_PICK_WHY, picks];
	}
	else if (refusal.code === TOO_LONG_CODE) {
		why = [
			refusalSentence(refusal),
			typeof onRepick === 'function' ? (
				<span className="query-refusal__picks">
					<Button disabled={disabled} onClick={onRepick}>{PICK_AGAIN_LABEL}</Button>
				</span>
			) : null,
		];
	} else why = [refusalSentence(refusal) || UNKNOWN_ELEMENT_EDIT];
	const heading = refusal.code === TOO_LONG_CODE ? TOO_LONG_TITLE : title;
	const own = className === undefined ? (inline ? null : 'items-page__alert') : className;
	const classes = ['query-refusal', inline ? 'query-refusal--inline' : null, own].filter(Boolean).join(' ');
	if (inline) {
		return (
			<Alert
				type="warning"
				showIcon
				role="alert"
				className={classes}
				message={heading}
				description={<div className="query-refusal__why">{why.filter(Boolean).map((line, index) => <div key={index}>{line}</div>)}</div>}
			/>
		);
	}
	return (
		<Guidance
			tone="caution"
			className={classes}
			title={heading}
			why={why.filter(Boolean)}
			action={typeof onRemove === 'function' ? { label: QUERY_REFUSAL_REMOVE_LABEL, onClick: onRemove } : null}
			links={[QUERY_REFUSAL_GUIDE]}
		/>
	);
}
